import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import {
  getAssignment,
  publishAssignment,
  gradeSubmission,
} from "../../api/assignmentApi";
import { useAuth } from "../../common/contexts/AuthContext";

const AssignmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [gradingSub, setGradingSub] = useState(null);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState("");
  const { user } = useAuth();
  const roleName = user?.role?.name || user?.Role?.name;
  const canGrade = roleName === "Admin" || roleName === "Teacher" || roleName === "SuperAdmin";

  const refresh = async () => {
    const data = await getAssignment(id);
    if (data?.success) {
      setAssignment(data.assignment);
      setSubmissions(data.submissions || []);
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  const handlePublish = async () => {
    await publishAssignment(id);
    toast.success("Assignment published");
    refresh();
  };

  const openGrade = (sub) => {
    setGradingSub(sub);
    setScore(sub.score);
    setFeedback(sub.feedback || "");
  };

  const saveGrade = async () => {
    try {
      await gradeSubmission(gradingSub.id, { score, feedback });
      toast.success("Submission graded");
      setGradingSub(null);
      refresh();
    } catch (e) {
      toast.error("Failed to grade submission");
    }
  };

  if (!assignment) return <Card>Loading...</Card>;

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <div className="flex justify-content-between align-items-start">
            <div>
              <h2 className="m-0">{assignment.title}</h2>
              <div className="mt-2 flex gap-2 align-items-center">
                <Tag
                  value={assignment.status}
                  severity={assignment.status === "published" ? "success" : "warning"}
                />
                {assignment.grade?.name && <span>Grade: {assignment.grade.name}</span>}
                {assignment.subject?.name && <span>· {assignment.subject.name}</span>}
                {assignment.due_date && (
                  <span>· Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                )}
                <span>· Max: {assignment.max_score}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {canGrade && assignment.status === "draft" && (
                <Button label="Publish" icon="pi pi-send" onClick={handlePublish} />
              )}
              {canGrade && (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  className="p-button-outlined"
                  onClick={() => navigate(`/index/assignments/${id}/edit`)}
                />
              )}
            </div>
          </div>
          {assignment.description && (
            <div className="mt-3 p-3 surface-100 border-round">{assignment.description}</div>
          )}
        </Card>
      </div>

      <div className="col-12">
        <Card title="Submissions">
          <DataTable value={submissions} emptyMessage="No submissions yet." stripedRows>
            <Column field="student.full_name" header="Student" />
            <Column
              field="status"
              header="Status"
              body={(row) => (
                <Tag
                  value={row.status}
                  severity={
                    row.status === "graded"
                      ? "success"
                      : row.status === "late"
                      ? "warning"
                      : row.status === "submitted"
                      ? "info"
                      : "secondary"
                  }
                />
              )}
            />
            <Column
              field="submitted_at"
              header="Submitted"
              body={(row) => (row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "—")}
            />
            <Column field="score" header="Score" body={(row) => (row.score != null ? `${row.score}/${assignment.max_score}` : "—")} />
            <Column
              header="Actions"
              body={(row) =>
                canGrade && (
                  <Button
                    icon="pi pi-check"
                    label="Grade"
                    className="p-button-text p-button-sm"
                    onClick={() => openGrade(row)}
                  />
                )
              }
            />
          </DataTable>
        </Card>
      </div>

      <Dialog
        header="Grade Submission"
        visible={!!gradingSub}
        onHide={() => setGradingSub(null)}
        style={{ width: "420px" }}
      >
        <div className="field">
          <label>Score (out of {assignment.max_score})</label>
          <InputNumber value={score} onValueChange={(e) => setScore(e.value)} className="w-full" />
        </div>
        <div className="field">
          <label>Feedback</label>
          <InputText value={feedback} onChange={(e) => setFeedback(e.target.value)} className="w-full" />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Cancel" className="p-button-text" onClick={() => setGradingSub(null)} />
          <Button label="Save" icon="pi pi-save" onClick={saveGrade} />
        </div>
      </Dialog>
    </div>
  );
};

export default AssignmentDetailPage;
